import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaEdit, FaTrashAlt, FaDownload, FaMapMarkerAlt, FaCalendarAlt, FaClock, FaPhoneAlt, FaMoneyBillWave, FaMusic } from 'react-icons/fa';
import config from '../config/config';
import "./EventDetailsPage.css";

const EventDetailsPage = () => {
    const { eventId } = useParams();
    const navigate = useNavigate();
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const token = localStorage.getItem("token");

    const apiUrl = `${config.API_URL}/api`;

    useEffect(() => {
        const fetchEventDetails = async () => {
            try {
                const response = await axios.get(`${apiUrl}/events/${eventId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setEvent(response.data);
                setLoading(false);
            } catch (err) {
                console.error("Error fetching event details:", err);
                setError("Impossible de charger les détails de l'événement.");
                setLoading(false);
            }
        };

        fetchEventDetails();
    }, [apiUrl, eventId, token]);

    const handleEventStatus = async (status) => {
        const confirmationMessage =
            status === "cancelled"
                ? "Voulez-vous vraiment réactiver cet événement ?"
                : "Voulez-vous vraiment annuler cet événement ?";
        const userConfirmed = window.confirm(confirmationMessage);

        if (userConfirmed) {
            try {
                await axios.put(`${apiUrl}/events/${eventId}/cancel`, {}, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                // Fetch updated info
                const response = await axios.get(`${apiUrl}/events/${eventId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setEvent(response.data);
            } catch (error) {
                console.error("Error updating event status:", error);
            }
        }
    };

    const handleDeleteEvent = async () => {
        const userConfirmed = window.confirm(
            "Êtes-vous sûr de vouloir supprimer cet événement ?"
        );

        if (userConfirmed) {
            try {
                await axios.delete(`${apiUrl}/events/${eventId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                navigate("/events");
            } catch (error) {
                console.error("Error deleting event:", error);
            }
        }
    };

    const downloadContract = (contractId, clientName) => {
        const sanitizedClientName = clientName.replace(/ /g, "_");
        const url = `${config.API_URL}/uploads/contracts/${sanitizedClientName}_${contractId}.docx`;
        window.open(url, "_blank");
    };

    if (loading) return <div className="event-details-loading">Chargement...</div>;
    if (error) return <div className="event-details-error">{error}</div>;
    if (!event) return <div className="event-details-error">Événement introuvable.</div>;

    return (
        <div className="event-details-container">
            {/* Back Button */}
            <button
                type="button"
                className="back-button"
                onClick={() => navigate('/events')}
            >
                <FaArrowLeft className="back-icon" />
                Retour
            </button>

            <div className="event-details-header">
                <div>
                    <h1>{event.name}</h1>
                    <span className={`status-badge ${event.status === 'cancelled' ? 'status-cancelled' : 'status-active'}`}>
                        {event.status === 'cancelled' ? 'Annulé' : 'Actif'}
                    </span>
                </div>
                <div className="header-actions">
                    <button
                        className="action-btn edit-btn"
                        onClick={() => navigate(`/edit-event/${event.id}`)}
                        title="Modifier"
                    >
                        <FaEdit /> Modifier
                    </button>
                    {event.contractId && (
                        <button
                            className="action-btn download-btn"
                            onClick={() => downloadContract(event.contractId, event.name)}
                            title="Télécharger le contrat"
                        >
                            <FaDownload /> Contrat
                        </button>
                    )}
                    <button
                        className="action-btn delete-btn"
                        onClick={handleDeleteEvent}
                        title="Supprimer"
                    >
                        <FaTrashAlt /> Supprimer
                    </button>
                </div>
            </div>

            <div className="grid-container">
                {/* Left Column - Core Info */}
                <div className="info-section">
                    <h2>Informations Générales</h2>

                    <div className="info-row">
                        <FaCalendarAlt className="info-icon" />
                        <div>
                            <p className="info-label">Date</p>
                            <p className="info-value">{new Date(event.date).toLocaleDateString()}</p>
                        </div>
                    </div>

                    <div className="info-row">
                        <FaClock className="info-icon" />
                        <div>
                            <p className="info-label">Horaire</p>
                            <p className="info-value">{event.startTime || "Non spécifié"} - {event.endTime || "Non spécifié"}</p>
                        </div>
                    </div>

                    <div className="info-row">
                        <FaMapMarkerAlt className="info-icon" />
                        <div>
                            <p className="info-label">Lieu</p>
                            <p className="info-value">{event.location}</p>
                        </div>
                    </div>

                    {event.description && (
                        <div className="info-description">
                            <p className="info-label">Description</p>
                            <p className="info-text">{event.description}</p>
                        </div>
                    )}
                </div>

                {/* Right Column - Contact & Financial Info */}
                <div className="side-section-wrapper">
                    {/* Phone Numbers */}
                    <div className="info-section">
                        <h2>Contacts</h2>
                        {(event.contract?.clientTel || event.phone1 || event.phone2 || event.phone3) ? (
                            <div className="phone-list">
                                {event.contract?.clientTel && (
                                    <div className="info-row compact">
                                        <FaPhoneAlt className="info-icon" />
                                        <div>
                                            <p className="info-label">Tél Contrat</p>
                                            <p className="info-value">{event.contract.clientTel}</p>
                                        </div>
                                    </div>
                                )}
                                {event.phone1 && (
                                    <div className="info-row compact">
                                        <FaPhoneAlt className="info-icon" />
                                        <div>
                                            <p className="info-label">Téléphone 1</p>
                                            <p className="info-value">{event.phone1}</p>
                                        </div>
                                    </div>
                                )}
                                {event.phone2 && (
                                    <div className="info-row compact">
                                        <FaPhoneAlt className="info-icon" />
                                        <div>
                                            <p className="info-label">Téléphone 2</p>
                                            <p className="info-value">{event.phone2}</p>
                                        </div>
                                    </div>
                                )}
                                {event.phone3 && (
                                    <div className="info-row compact">
                                        <FaPhoneAlt className="info-icon" />
                                        <div>
                                            <p className="info-label">Téléphone 3</p>
                                            <p className="info-value">{event.phone3}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <p className="info-empty">Aucun numéro de téléphone fourni.</p>
                        )}
                    </div>

                    {/* Contract Box */}
                    {event.contract && (
                        <div className="info-section highlight">
                            <h2>Détails du Contrat</h2>
                            <div className="info-row compact">
                                <FaMusic className="info-icon" />
                                <div>
                                    <p className="info-label">Nb. Musiciens</p>
                                    <p className="info-value">{event.contract.numberMusicians}</p>
                                </div>
                            </div>
                            <div className="info-row compact">
                                <FaMoneyBillWave className="info-icon" />
                                <div>
                                    <p className="info-label">Prix Total</p>
                                    <p className="info-value">{event.contract.price} DT</p>
                                </div>
                            </div>
                            <div className="info-row compact">
                                <FaMoneyBillWave className="info-icon" />
                                <div>
                                    <p className="info-label">Avance Payée</p>
                                    <p className="info-value">{event.contract.prepayment} DT</p>
                                </div>
                            </div>
                            <div className="info-row compact">
                                <FaMoneyBillWave className="info-icon alert" />
                                <div>
                                    <p className="info-label">Reste à payer</p>
                                    <p className="info-value alert-text">
                                        {Number(event.contract.price) - Number(event.contract.prepayment)} DT
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="footer-actions">
                <button
                    className={`btn-large ${event.status === 'cancelled' ? 'reactivate' : 'cancel'}`}
                    onClick={() => handleEventStatus(event.status)}
                >
                    {event.status === "cancelled"
                        ? "Réactiver l'événement"
                        : "Annuler l'événement"}
                </button>
            </div>

        </div>
    );
};

export default EventDetailsPage;
